import { useEffect, useRef, type RefObject } from 'react';

/**
 * Shared overlay a11y hook (Story 4.4, UX-DR6 / UX-DR-A11Y / UX-DR15). Adopted by
 * every modal-style overlay ({@link Drawer}, {@link ConfirmModal}, SettingsPanel)
 * in place of their duplicated Esc/focus effects. It provides the full overlay
 * keyboard contract:
 *
 *  - **Esc** closes the topmost overlay (calls the latest `onClose`).
 *  - **Initial focus** moves to the panel on open.
 *  - **Return focus** restores the opener on close (unmount).
 *  - **Focus trap** — Tab / Shift+Tab cycle within the panel's focusable
 *    elements so focus can never escape the dialog while it is open.
 *
 * `onClose` is read through a ref so an inline arrow (new identity each render)
 * never re-runs the effect — the panel is focused ONCE on open and the opener is
 * restored ONCE on close, preserving the prior single-run semantics.
 */
export function useDialogA11y(
  panelRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    panel?.focus();

    // The tab-reachable elements, computed live on each Tab so it tracks dynamically
    // added/removed controls (e.g. an inline error, a busy/disabled button). Disabled
    // controls and tabindex="-1" are excluded by the selector itself.
    const focusable = (): HTMLElement[] => {
      if (!panel) return [];
      return Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
    };

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;

      const nodes = focusable();
      const active = document.activeElement as HTMLElement | null;
      // With nothing focusable inside, keep focus pinned to the panel itself.
      if (nodes.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;

      if (e.shiftKey) {
        // Backward past the first (or focus sitting on the panel/outside) wraps to the last.
        if (active === first || active === panel || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || active === panel || !panel.contains(active)) {
        // Forward past the last (or focus on the panel/outside) wraps to the first.
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
    // `panelRef` is a stable ref object → this runs once on open / cleans up once
    // on close. `onClose` is intentionally read via the ref (see above).
  }, [panelRef]);
}
